import {Badge} from '@mantine/core';
import {Card} from 'src/design/atoms/Card';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconAlertTriangle, IconMapPinOff} from '@tabler/icons-react';
import React, {useMemo} from 'react';
import type {InspectionResponse} from 'src/api/ProactApiClient';
import {ComplianceStateEnum} from 'src/api/ProactApiClient';
import {DeficiencyCard} from 'src/app/inspections/DeficiencyCard';
import {formatDeviceLocation} from 'src/app/inspections/deviceRollup';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Subtitle} from 'src/design/atoms/Typography';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {InlineStat} from 'src/design/molecules/InlineStat';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {Pill} from 'src/design/atoms/Pill';
import {useGetInspectionDeficiencyListQuery} from 'src/redux/queries/deficiency';
import {useGetInspectionScopeDevicesQuery} from 'src/redux/queries/inspectionRecord';
import {InspectionStateEnumPill} from 'src/shared/constants/PillConstants';
import {InspectionDocumentsCard} from '../../shared/components/InspectionDocumentsCard';

export interface InspectionMyCompletedScreenProps {
  inspection: InspectionResponse;
}

const DEFICIENCY_PAGE_LENGTH = 500;

/**
 * Read-only completed-inspection review for the guided flow's assignee — same 880px
 * column chrome as overview/confirm/complete, not full-bleed like execute.
 */
export const InspectionMyCompletedScreen: React.FC<InspectionMyCompletedScreenProps> = ({
  inspection,
}) => {
  const {data: deficiencyData} = useGetInspectionDeficiencyListQuery({
    id: inspection.id,
    inspectionId: inspection.id,
    start: 0,
    length: DEFICIENCY_PAGE_LENGTH,
  });
  const deficiencies = deficiencyData?.data ?? [];

  const {data: devices} = useGetInspectionScopeDevicesQuery(inspection.id);
  const noAccessDevices = useMemo(
    () => (devices ?? []).filter(d => d.complianceState === ComplianceStateEnum.NoAccess),
    [devices]
  );

  return (
    <Stack gap="lg">
      <Card shadow="sm" withBorder padding="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <Subtitle
            text={`${inspection.siteName} — ${inspection.inspectionTypeName}`}
            style={{flex: 1}}
          />
          <Pill {...InspectionStateEnumPill(inspection.state)} />
        </Group>
        <Divider my="md" color="var(--mantine-color-gray-2)" />
        <SimpleGrid cols={{base: 2, sm: 3}} spacing="xl" verticalSpacing="md">
          <InlineStat label="Pass" value={inspection.passCount} />
          <InlineStat label="Failed devices" value={inspection.failCount} />
          <InlineStat label="No access" value={inspection.noAccessCount} />
        </SimpleGrid>
      </Card>

      <InfoAlert>
        This is a read-only summary of your completed inspection — deficiency resolution and
        other follow-up work happen outside this view.
      </InfoAlert>

      <SectionCard
        title="Deficiencies"
        icon={<IconAlertTriangle size={16} />}
        rightSection={
          deficiencies.length > 0 ? (
            <Badge size="lg" variant="light" color="gray" circle>
              {deficiencies.length}
            </Badge>
          ) : undefined
        }>
        <div style={{padding: 'var(--mantine-spacing-md)'}}>
          {deficiencies.length === 0 ? (
            <NoItemsPanel noItemsText="No deficiencies — all devices passed." />
          ) : (
            <Stack gap="sm">
              <Text size="xs" c="dimmed">
                Auto-generated from failed devices
              </Text>
              {deficiencies.map(d => (
                <DeficiencyCard key={d.id} deficiency={d} />
              ))}
            </Stack>
          )}
        </div>
      </SectionCard>

      {noAccessDevices.length > 0 && (
        <SectionCard
          title="No-access devices"
          icon={<IconMapPinOff size={16} />}
          rightSection={
            <Badge size="lg" variant="light" color="gray" circle>
              {noAccessDevices.length}
            </Badge>
          }>
          <Stack gap={0} p="md">
            {noAccessDevices.map(device => {
              const location = formatDeviceLocation(device);
              // Join type · location, dropping unknowns instead of rendering
              // dangling "—" placeholders at the row's edge.
              const meta = [
                device.equipmentTypeName,
                location === '—' ? undefined : location,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <Group
                  key={device.deviceId}
                  gap="sm"
                  wrap="nowrap"
                  py="xs"
                  style={{borderBottom: '1px solid var(--mantine-color-gray-2)'}}>
                  <Text size="sm" fw={500} truncate style={{flex: 1, minWidth: 0}}>
                    {device.name ?? `Device #${device.deviceId}`}
                  </Text>
                  {meta && (
                    <Text size="xs" c="dimmed" truncate style={{maxWidth: 320}}>
                      {meta}
                    </Text>
                  )}
                </Group>
              );
            })}
          </Stack>
        </SectionCard>
      )}

      <InspectionDocumentsCard inspectionId={inspection.id} />
    </Stack>
  );
};
